// ---------------------------------------------------------------------------
// The room.
//
// It has five jobs, in this order:
//
//   1. Get the player into the room: look the code up, clear bot protection if
//      it is switched on.
//   2. Ask for the connection, and hand `send` down to whichever screen is
//      current. The socket itself is the app shell's — `RoomConnectionProvider`
//      sits above the router, because joining by code is started on the home
//      screen and a connection that died with the navigation is what put a
//      second "Getting you a seat" screen between a player and their room.
//      Nothing here opens a socket of its own; there is only ever one.
//   3. Route on phase. The table below is exhaustive against GamePhase, so a new
//      phase is a type error here rather than a blank screen in production.
//   4. Put the current screen inside `RoomShell`, which owns everything that
//      outlives a phase — the site header, the one chat panel, the leave
//      confirmation — and cross it over the last one.
//   5. Host the two overlays, above whatever screen is showing. No screen
//      renders them itself, because both are about the connection rather than
//      about the round.
//
// Bot protection is required on a fresh join and skipped on a reconnect: a
// player whose wifi blipped should not have to clear a challenge to get back
// into a round they are halfway through. The server enforces the same rule.
//
// TWO CROSSFADES, NESTED, AND THEY ARE NOT THE SAME BOUNDARY. The inner one is
// job 4: one phase screen giving way to the next, inside the room. The outer
// one is this page's own arrival — the seat notice giving way to the room, and
// the room fading up from the home screen that dissolved on the way here. Both
// are `Crossfade` and both run for the same length, because a player crossing
// from a menu into a lobby and then from a lobby into a board should not be
// able to tell that two different boundaries drew it.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_NAME_MAX, validateDisplayName } from '@countdown/shared';
import type { BotDifficulty, ClientMessage, GamePhase } from '@countdown/shared';
import { useRoomConnection, type RoomTarget } from '../../hooks/roomConnection.js';
import { acquireTurnstileToken, useSiteConfig } from '../../hooks/useSiteConfig.js';
import { useAccount } from '../../hooks/useAccount.js';
import { useGameStore } from '../../store/gameStore.js';
import { usePlayerStore } from '../../store/playerStore.js';
import { lookupRoom } from '../../lib/api.js';
import { Button } from '../../components/primitives/Button.js';
import { Card } from '../../components/primitives/Card.js';
import { CodeInput } from '../../components/primitives/CodeInput.js';
import { ServerPauseOverlay } from '../../components/overlays/ServerPauseOverlay.js';
import { DisconnectSheet } from '../../components/overlays/DisconnectSheet.js';
import { AppHeader } from '../../components/layout/AppHeader.js';
import { RoomShell } from '../../components/layout/RoomShell.js';
import { Crossfade, PhaseCrossfade } from '../../components/layout/PhaseCrossfade.js';
import { LobbyScreen } from './LobbyScreen.js';
import { RoundIntroScreen } from './RoundIntroScreen.js';
import { LettersSelectionScreen } from './LettersSelectionScreen.js';
import { LettersPlayScreen } from './LettersPlayScreen.js';
import { NumbersDrawingScreen } from './NumbersDrawingScreen.js';
import { NumbersSelectionScreen } from './NumbersSelectionScreen.js';
import { NumbersPlayScreen } from './NumbersPlayScreen.js';
import { ConundrumPlayScreen } from './ConundrumPlayScreen.js';
import { RevealScreen } from './RevealScreen.js';
import { EndGameScreen } from './EndGameScreen.js';
import type { RoomScreenProps } from './types.js';
import { DISCONNECT_GRACE_MS, useHeldTrue } from '../../hooks/useHeldTrue.js';
import { SEAT_NOTICE_MIN_MS, useMinimumOnScreen } from '../../hooks/useMinimumOnScreen.js';

/** What HomeScreen passes through the navigation. */
export interface RoomNavState {
  /**
   * A page has already dissolved into this one, so this page must not dissolve
   * in again.
   *
   * **Two dissolves back to back through the same background is the stutter
   * this exists to stop.** The home screen fades its content to the page
   * background over `FADE_MS` and then navigates; this page arrived at opacity 0
   * and faded up over another 150ms. Both ends of that are the same colour, so
   * what a player saw was one long flat middle with a blank in it — a press,
   * then nothing, then the lobby. Isaac reported it as the transition not being
   * smooth, twice.
   *
   * Set by every route out of the home screen, so it is a fact about the
   * navigation rather than a guess from `location.state` being non-null — a plain
   * join by code carries no other state, and would otherwise be indistinguishable
   * from somebody opening a shared link cold.
   *
   * A cold arrival — typed URL, reload, shared link — has nothing to dissolve
   * from and still fades in.
   */
  dissolved?: boolean;
  /** Set when this navigation created the room, so the join can skip a lookup. */
  justCreated?: boolean;
  asSpectator?: boolean;
  /**
   * "Play solo": seat one bot at this difficulty on arrival.
   *
   * Adding a bot is a host action over the socket, and creating a room does not
   * open one on the home screen — a create navigates as soon as the room exists,
   * where a join by code waits for its seat — so it lands here. It stops at
   * seating: the game is started from the
   * lobby like every other game, which is also where the difficulty, the extra
   * bots and the round mix are set.
   */
  soloBot?: BotDifficulty;
}

// Exhaustive by construction: Record<GamePhase, ...> fails to compile if a phase
// is added to the union without a screen to show for it.
const SCREEN_FOR_PHASE: Record<GamePhase, (props: RoomScreenProps) => React.ReactElement> = {
  lobby: LobbyScreen,
  'round-intro': RoundIntroScreen,
  'letters-selection': LettersSelectionScreen,
  'letters-play': LettersPlayScreen,
  'letters-reveal': RevealScreen,
  'numbers-selection': NumbersSelectionScreen,
  'numbers-drawing': NumbersDrawingScreen,
  'numbers-play': NumbersPlayScreen,
  'numbers-reveal': RevealScreen,
  'conundrum-play': ConundrumPlayScreen,
  'conundrum-reveal': RevealScreen,
  'game-over': EndGameScreen,
};

/**
 * Which of this page's own screens is up, before any phase is involved.
 *
 * One value rather than a run of early returns with a `return` in each of them,
 * because two of these — the seat notice and the room — have to be JOINED
 * rather than cut between, and a join has to be told what it is leaving. The
 * order they are worked out in below IS the precedence the early returns used
 * to have, and there is only one copy of it.
 */
type RoomStage =
  | 'dead-end'
  | 'no-room'
  | 'name'
  | 'challenge-failed'
  | 'refused'
  | 'joining'
  | 'room';

/**
 * How long the first join waits for Clerk before going ahead as a guest.
 *
 * Giving up is no longer permanent: the connection re-sends its join when the
 * account layer reports a session, so a slow script costs a guest join for a few
 * seconds rather than for the life of the room.
 */
const CLERK_WAIT_MS = 4_000;

/**
 * Everything before there is a room to be in: the join, and the four ways it
 * can fail. The header is on these too — a player looking at "no room with that
 * code" needs the same way home as everybody else.
 */
function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full flex-col">
      <AppHeader />
      <main className="flex flex-1 items-center justify-center p-6">
        <Card className="w-full max-w-md">{children}</Card>
      </main>
    </div>
  );
}

export function RoomPage() {
  const params = useParams<{ code: string }>();
  const roomCode = (params.code ?? '').toUpperCase();
  const location = useLocation();
  const navigate = useNavigate();
  // Memoised on the state itself, not rebuilt per render. `?? {}` mints a fresh
  // object every time the entry carries nothing, and the solo effect below
  // depends on this identity — an unmemoised default would re-run it on every
  // render of every room that was not navigated to with any state at all.
  const nav = useMemo(() => (location.state ?? {}) as RoomNavState, [location.state]);

  const { config, loading: configLoading } = useSiteConfig();
  const account = useAccount();

  const state = useGameStore((store) => store.state);
  const deadEnd = useGameStore((store) => store.deadEnd);
  const joinError = useGameStore((store) => store.joinError);
  const quality = useGameStore((store) => store.quality);

  // Held back rather than read straight, so backgrounding the browser on a
  // phone and coming back does not flash a disconnect notice for a quarter of a
  // second on the way through. `quality` itself is not debounced — see the note
  // in `useHeldTrue`.
  const droppedForLongEnough = useHeldTrue(
    quality.status === 'reconnecting',
    DISCONNECT_GRACE_MS,
  );

  const displayName = usePlayerStore((store) => store.displayName);
  const setDisplayName = usePlayerStore((store) => store.setDisplayName);
  const reconnectToken = usePlayerStore((store) => store.reconnectTokens[roomCode]);

  /** What is in the name field, until it is good enough to keep. */
  const [nameDraft, setNameDraft] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  /** Null until the challenge has been settled one way or the other. */
  const [challenge, setChallenge] = useState<{ token: string | undefined } | null>(null);
  const [challengeAttempt, setChallengeAttempt] = useState(0);

  // A reconnect is any join this device already holds a token for. It skips bot
  // protection, which is the whole point of the token: a player whose wifi
  // blipped should not have to clear a challenge to get back into a round they
  // are halfway through.
  const isReconnect = reconnectToken !== undefined;

  // Neither a reconnect nor the player who just created the room needs a lookup
  // or a challenge. Derived rather than assigned from an effect, so the first
  // render already knows and the join is not held up for a frame.
  const skipGate = nav.justCreated || isReconnect;

  useEffect(() => {
    if (skipGate) return;

    // The answer is discarded: with room passwords gone, the only thing a
    // lookup still decides is whether the code exists at all, so that a wrong
    // code fails here with something to read rather than at the socket.
    let live = true;
    lookupRoom(roomCode)
      .catch((error: unknown) => {
        if (live) {
          setLookupError(error instanceof Error ? error.message : 'That room could not be found.');
        }
      });

    return () => {
      live = false;
    };
  }, [roomCode, skipGate]);

  const turnstileDone = skipGate || challenge !== null;

  // One challenge per join attempt: tokens are single-use and the server
  // remembers the ones it has already spent.
  useEffect(() => {
    if (skipGate || configLoading || challenge !== null) return;

    let live = true;
    acquireTurnstileToken(config)
      .then((token) => {
        if (live) setChallenge({ token });
      })
      .catch((error: unknown) => {
        if (!live) return;
        setTurnstileError(error instanceof Error ? error.message : 'Bot protection failed.');
      });

    return () => {
      live = false;
    };
  }, [config, configLoading, challenge, skipGate, challengeAttempt]);

  // --- The account's session token -------------------------------------------
  // Not held here at all. `freshToken` is handed to the connection, which calls
  // it at the moment it sends a join — a Clerk session JWT lives about a minute
  // and a join is sent again on every socket.io reconnect, so a token settled
  // once per page is stale by its second use.
  //
  // A page that settled it before Clerk answered was worse than stale: it joined
  // as a guest and could never mint another, and because the seat's identity is
  // written once, that guest was permanent across every later reload.
  //
  // What is still waited for is an *answer*, and only for the first join, and
  // only for a moment: playing must not become dependent on a third-party script
  // that may never arrive. If the answer comes in afterwards, `signedIn` flipping
  // is what re-sends the join.
  const [clerkTimedOut, setClerkTimedOut] = useState(false);

  useEffect(() => {
    if (account.ready) return;
    const giveUp = setTimeout(() => setClerkTimedOut(true), CLERK_WAIT_MS);
    return () => clearTimeout(giveUp);
  }, [account.ready]);

  const ready =
    displayName.length > 0 &&
    turnstileDone &&
    (account.ready || clerkTimedOut) &&
    lookupError === null;

  // The session token and the sign-in are not named here: the connection reads
  // them from the account context itself, so a Clerk provider handing out a new
  // function cannot restart a live socket. What is named is the whole of what
  // decides the socket's identity.
  const joinTarget = useMemo<RoomTarget | null>(() => {
    if (!ready) return null;
    return {
      roomCode,
      turnstileToken: challenge?.token,
      asSpectator: nav.asSpectator,
    };
  }, [ready, roomCode, challenge?.token, nav.asSpectator]);

  // The connection is the app shell's, not this page's, and that is what stops
  // joining by code being two screens: the home screen opens it and navigates
  // only once there is a room, so on that path this page's first render already
  // has state and the branch below never draws. Asking for a room that is
  // already connected is a no-op — see hooks/roomConnection.tsx.
  //
  // Arriving on a shared link, or reloading, still starts from nothing here, and
  // every gate above is exactly as it was for that case.
  const { connection, join } = useRoomConnection();

  useEffect(() => {
    if (joinTarget === null) return;
    join(joinTarget);
  }, [joinTarget, join]);

  const send = useCallback(
    (message: ClientMessage) => {
      connection.send(message);
    },
    [connection],
  );

  // --- "Play solo" -----------------------------------------------------------
  // The home screen creates the room and navigates; seating the bot has to
  // happen from inside the room, because it is a host action over a socket this
  // page is the only owner of.
  //
  // Asked for exactly once, and never again. Deciding what to send from how many
  // bots have ARRIVED re-sends on every state broadcast until the first one
  // lands, and the player ends up with a stack of "the room is full".
  //
  // The ref alone was not "never again", because the request does not live in
  // this component — it lives in the history entry, and a history entry outlives
  // every mount that reads it. Reloading a solo room, or walking back to it,
  // replays `soloBot` against a room that already has its opponent and seats
  // another one, every time. So the instruction is CONSUMED: the entry is
  // rewritten without it the moment it is acted on, and from then on a solo
  // lobby is the same object as a room somebody built by hand.
  //
  // `replace` rather than a push, and the rest of the state is carried over
  // untouched — `justCreated` is what lets the join skip the room lookup, and
  // dropping it here would put the gate in front of a player already inside.
  //
  // The second guard is for the entry that is acted on before it can be
  // rewritten: nothing is seated into a room that already has a bot in it.
  const soloBotRequested = useRef(false);

  useEffect(() => {
    const difficulty = nav.soloBot;
    if (difficulty === undefined || soloBotRequested.current) return;
    if (!state || !state.youAreHost || state.phase !== 'lobby') return;

    soloBotRequested.current = true;

    const { soloBot: _consumed, ...rest } = nav;
    navigate(location.pathname, { replace: true, state: rest });

    if (state.players.some((player) => player.isBot)) return;
    send({ type: 'add-bot', difficulty });
  }, [nav, state, send, navigate, location.pathname]);

  // --- Which screen is up ----------------------------------------------------
  //
  // The precedence the early returns used to carry, written once. Everything
  // below reads `stage` and nothing re-derives any of these conditions, because
  // two copies of a precedence chain is two chains that can disagree about
  // which screen a player is looking at.
  const arrived: RoomStage =
    deadEnd !== null
      ? 'dead-end'
      : lookupError !== null
        ? 'no-room'
        : displayName.length === 0
          ? 'name'
          : turnstileError !== null
            ? 'challenge-failed'
            : joinError !== null && state === null
              ? 'refused'
              : state === null
                ? 'joining'
                : 'room';

  // The seat notice's floor, and it is a floor rather than a delay: the fast
  // path never draws the notice, so it never waits for it either. See
  // `useMinimumOnScreen`.
  //
  // What it is told is whether the notice is ON SCREEN, not merely whether
  // there is no state yet — those are different, and the difference is a real
  // bug avoided. A player arriving on a shared link sits on the name form with
  // no state for as long as they take to type, and a floor that started
  // counting there would have expired long before the notice was drawn.
  //
  // It extends over the ROOM only. Holding it over a dead end or a refusal
  // would be a screen with a reason on it, delayed, which is the opposite of
  // what a floor is for.
  const holdingSeatNotice = useMinimumOnScreen(arrived === 'joining', SEAT_NOTICE_MIN_MS);
  const stage: RoomStage = arrived === 'room' && holdingSeatNotice ? 'joining' : arrived;

  function renderStage(key: RoomStage): React.ReactNode {
    switch (key) {
      case 'dead-end': {
        if (deadEnd === null) return null;
        // `displaced` is the odd one out and reads as the mildest, because it
        // is: the seat is still this player's and the tab that took it is
        // theirs too. Coming back here takes it straight back.
        const heading =
          deadEnd.kind === 'banned'
            ? 'You are banned from this room'
            : deadEnd.kind === 'kicked'
              ? 'You were removed from the room'
              : deadEnd.kind === 'displaced'
                ? 'You joined this room somewhere else'
                : 'The room closed';
        return (
          <Centred>
            <h1 className="text-2xl font-bold">{heading}</h1>
            {deadEnd.reason ? <p className="mt-2 text-ink-muted">{deadEnd.reason}</p> : null}
            <Button variant="primary" className="mt-5" onClick={() => navigate('/')}>
              Back to the start
            </Button>
          </Centred>
        );
      }

      case 'no-room':
        return (
          <Centred>
            <h1 className="text-2xl font-bold">No room with that code</h1>
            {/* The one thing the screen does not already say: a code that used
                to work can stop working. Everything else was a description of
                the two buttons underneath it. */}
            <p className="mt-2 text-ink-muted">A code stops working when its room empties.</p>
            <div className="mt-5 flex gap-3">
              <Button variant="primary" onClick={() => navigate('/')}>
                Start a game
              </Button>
              <Button variant="outline" onClick={() => navigate('/browse')}>
                Browse public games
              </Button>
            </div>
          </Centred>
        );

      // A player who arrived on a shared link has no name yet, so the field is
      // here.
      //
      // It used to be a button back to the home screen and a paragraph
      // explaining the round trip. One field and the button that uses it is the
      // whole interaction, and a screen that sends you somewhere else to type
      // six characters is two screens for one action.
      //
      // The room code is deliberately not named here. It was, and this is the
      // one screen a player is likely to be sitting on with the window shared
      // or a phone in somebody's eyeline — the code is what lets a stranger
      // walk in.
      case 'name': {
        const chosen = validateDisplayName(nameDraft);
        return (
          <Centred>
            <form
              className="flex flex-col gap-2.5"
              onSubmit={(event) => {
                event.preventDefault();
                if (chosen.ok) setDisplayName(chosen.value);
              }}
            >
              <label htmlFor="join-name" className="label-mono">
                name
              </label>
              <input
                id="join-name"
                autoFocus
                value={nameDraft}
                onChange={(event) => setNameDraft(event.target.value)}
                maxLength={DISPLAY_NAME_MAX}
                autoComplete="nickname"
                spellCheck={false}
                className="min-h-12 rounded-[11px] border border-line bg-surface-2 px-3.5 text-[17px] font-medium text-ink transition-colors duration-150 outline-none placeholder:text-ink-dim hover:border-line-strong focus:border-blue-400 focus:bg-surface"
              />
              <Button type="submit" variant="primary" size="lg" block disabled={!chosen.ok}>
                Join
              </Button>
            </form>
          </Centred>
        );
      }

      case 'challenge-failed':
        return (
          <Centred>
            <h1 className="text-2xl font-bold">Bot protection did not clear</h1>
            <p className="mt-2 text-ink-muted">{turnstileError}</p>
            <Button
              variant="primary"
              className="mt-5"
              onClick={() => {
                setTurnstileError(null);
                setChallenge(null);
                // Bump the attempt so the effect re-runs even though nothing
                // else about the join changed.
                setChallengeAttempt((attempt) => attempt + 1);
              }}
            >
              Try again
            </Button>
          </Centred>
        );

      case 'refused':
        return (
          <Centred>
            <h1 className="text-2xl font-bold">Could not join the room</h1>
            <p className="mt-2 text-ink-muted">{joinError?.message}</p>
            <div className="mt-5 flex gap-3">
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to the start
              </Button>
            </div>
          </Centred>
        );

      // Deliberately not a heading and a paragraph about joining.
      //
      // **This is on neither the join-by-code path nor the create path any
      // more.** The home screen holds its own button down through the lookup,
      // the challenge and the socket, and navigates only once there is a room;
      // the connection is the shell's and outlives the navigation, so arriving
      // from there means `state` is already set and this is never drawn. That
      // is the flash it used to be, on both buttons.
      //
      // What is left for it is arriving cold: a shared link, a reload, or a
      // link walked back to. There is a real wait on those and it is this
      // page's own, so it keeps the treatment that picks the home screen's
      // gesture up mid-stride — the same boxes holding the same code, still
      // inert, the same button still spinning — rather than a heading
      // announcing a new step. And once it is up it stays up long enough to be
      // read: see the floor above.
      case 'joining':
        return (
          <Centred>
            <div className="flex flex-col items-center gap-4">
              <div className="-ml-2">
                <CodeInput
                  value={roomCode}
                  onChange={() => {}}
                  disabled
                  describedBy="joining-note"
                />
              </div>
              <Button variant="outline" size="lg" loading disabled>
                Join
              </Button>
              <p id="joining-note" className="text-[13px] leading-[18px] text-ink-dim">
                Getting you a seat.
              </p>
              <Link to="/" className="text-[13px] text-blue-200 underline">
                Go back
              </Link>
            </div>
          </Centred>
        );

      case 'room': {
        // Guarded rather than asserted: for the length of a fade this is asked
        // for the screen on its way OUT as well as the one coming in, and a
        // room that ended in a dead end has no state left to draw.
        if (state === null) return null;

        const round = state.rounds[state.currentRoundIndex] ?? null;
        const ownAnswer = round?.ownSubmission ?? null;

        // The length the server would score if this player never came back.
        // Real, rather than a placeholder, because the sheet's whole job is to
        // say what is saved.
        const savedAnswerLength =
          ownAnswer === null
            ? null
            : ownAnswer.kind === 'letters'
              ? ownAnswer.word.length
              : ownAnswer.declaredLength;

        return (
          <>
            <RoomShell state={state} send={send}>
              {/* Asked for by phase rather than handed a screen, because the
                  fade renders two of them: the one arriving and, for a moment,
                  the one it is replacing. */}
              <PhaseCrossfade
                phaseKey={state.phase}
                render={(phase) => {
                  const Showing = SCREEN_FOR_PHASE[phase];
                  return <Showing state={state} send={send} connection={connection} />;
                }}
              />
            </RoomShell>

            {/* A server pause stops everyone, so it takes the whole screen. One
                player dropping stops nobody, so it gets a sheet and leaves the
                board live above it. The difference is deliberate and
                load-bearing. */}
            {state.serverPaused ? (
              <ServerPauseOverlay
                clock={round?.clock ?? null}
                waitingPlayerCount={
                  state.players.filter((player) => player.role === 'player').length
                }
              />
            ) : null}

            {droppedForLongEnough && !state.serverPaused ? (
              <DisconnectSheet
                phase={state.phase}
                clock={round?.clock ?? null}
                savedAnswerLength={savedAnswerLength}
                pauseOnDisconnect={state.settings.pauseOnDisconnect}
              />
            ) : null}
          </>
        );
      }
    }
  }

  // `h-full` rather than the phase crossfade's own box: this one is the room
  // page's outermost element, standing where `#root`'s child used to, and every
  // screen under it asks for `min-h-full` — which resolves against nothing if
  // the box it is inside has no height of its own.
  //
  // **One dissolve, not two.** `fadeIn` used to be unconditional, as the far
  // half of the home screen's fade OUT — but nothing above the router can hold
  // both pages at once, so the two halves do not overlap: they run end to end
  // through the page's own background, which is the same colour on both sides.
  // The result was a press, then a fade to nothing, then a blank, then a fade
  // back up — about 340ms with a flat middle, which reads as a stutter rather
  // than as a dissolve.
  //
  // So a page that was dissolved INTO simply appears; the home screen's fade is
  // the whole transition and the lobby is what it reveals. A cold arrival has
  // nothing to dissolve from and still fades in. See `RoomNavState.dissolved`.
  //
  // Reduced motion collapses either to a cut, because index.css clamps every
  // transition on the page and nothing here branches on the setting.
  return (
    <Crossfade
      className="relative h-full"
      contentKey={stage}
      fadeIn={nav.dissolved !== true}
      render={(key) => renderStage(key as RoomStage)}
    />
  );
}
